import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { Copy, Share2, Gift, Check, Store, CalendarDays, Users, RefreshCw, AlertCircle } from 'lucide-react'
import Loader from 'components/_UI/Loader'
import ToastContainer from 'components/_UI/ToastsContainer'
import useToast from 'hooks/useToast'
import useShare from 'hooks/useShare'
import { copyToClipboard } from 'helpers/clipboard'
import date from 'helpers/date'
import colors from 'theme/colors'
import breakpoints from 'theme/breakpoints'
import paths from 'routes/paths'
import affiliateApi from 'api/requests/affiliate'
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';

const ACTIVE_GREEN = '#15703C'

const STATUS_LABEL = {
    active: 'Ativa',
    expired: 'Expirada',
}

const STATUS_COLOR = {
    active: ACTIVE_GREEN,
    expired: colors.night,
}

const PARTNER_TYPE_LABEL = {
    producer: 'Produtor',
    establishment: 'Estabelecimento',
}

const PARTNER_TYPE_ICON = {
    producer: CalendarDays,
    establishment: Store,
}

export default function Affiliate() {
    const { toasts, toast } = useToast()
    const { share } = useShare()

    const [isLoading, setIsLoading] = useState(true)
    const [code, setCode] = useState(null)
    const [linkError, setLinkError] = useState(null)
    const [summary, setSummary] = useState(null)
    const [summaryError, setSummaryError] = useState(null)
    const [copied, setCopied] = useState(false)
    const copiedTimeout = useRef(null)

    const link = code ? `${window.location.origin}${paths.partner.createAccount}?ref=${code}` : null

    useEffect(() => {
        loadData()
        return () => clearTimeout(copiedTimeout.current)
    }, [])

    async function loadData() {
        await Promise.all([loadLink(), loadSummary()])
        setIsLoading(false)
    }

    async function loadLink() {
        try {
            const { data } = await affiliateApi.getLink()

            setCode(data?.code || null)
            setLinkError(null)
        } catch ({ response }) {
            setCode(null)
            setLinkError(response?.data?.message || 'Não foi possível gerar o link de indicação.')
        }
    }

    async function loadSummary() {
        try {
            const { data } = await affiliateApi.getSummary()

            setSummary(data)
            setSummaryError(null)
        } catch ({ response }) {
            setSummary(null)
            setSummaryError(response?.data?.message || 'Não foi possível carregar o resumo de indicações.')
        }
    }

    function handleCopy() {
        if (!link) return

        copyToClipboard(link, () => {
            toast.success('Link copiado com sucesso!', { position: 'top-right' })
            setCopied(true)
            clearTimeout(copiedTimeout.current)
            copiedTimeout.current = setTimeout(() => setCopied(false), 2500)
        })
    }

    function handleShare() {
        if (!link) return

        share({
            title: 'Indique e ganhe',
            text: 'Cadastre-se como produtor ou estabelecimento usando meu link de indicação:',
            url: link,
        })
    }

    if (isLoading) {
        return <Loader />
    }

    const referrals = summary?.referrals || []
    const activeCount = summary?.active_referrals_count || 0

    return (
        <>
            <ToastContainer toasts={toasts} />

            <Card>
                <CardTop>
                    <AccentIconBox>
                        <Gift size={20} color={panelAccent(colors.secondaryPurple)} />
                    </AccentIconBox>
                    <div>
                        <CardTitle>Seu link de indicação</CardTitle>
                        <CardHint>Compartilhe com quem quer criar eventos ou cadastrar um estabelecimento.</CardHint>
                    </div>
                </CardTop>

                {
                    linkError ? (
                        <InlineError>
                            <AlertCircle size={18} color={colors.danger} />
                            <InlineErrorText>
                                <strong>{linkError}</strong>
                                <span>Sem o código não há link para compartilhar.</span>
                            </InlineErrorText>
                            <GhostButton onClick={loadLink}>
                                <RefreshCw size={14} />
                                Tentar novamente
                            </GhostButton>
                        </InlineError>
                    ) : (
                        <>
                            <LinkField title={link}>{link}</LinkField>

                            <ShareActions>
                                <PrimaryButton onClick={handleCopy} $done={copied}>
                                    {copied ? <Check size={16} /> : <Copy size={16} />}
                                    {copied ? 'Link copiado' : 'Copiar link'}
                                </PrimaryButton>
                                <SecondaryButton onClick={handleShare}>
                                    <Share2 size={16} />
                                    Compartilhar
                                </SecondaryButton>
                            </ShareActions>

                            <CodeRow>
                                Ou informe o código
                                <CodeChip>{code}</CodeChip>
                            </CodeRow>
                        </>
                    )
                }
            </Card>

            <Card>
                <SectionLabel>Como funciona</SectionLabel>

                <RuleItem>
                    <RuleIconBox>
                        <CalendarDays size={17} color={panelAccent(colors.secondaryPurple)} />
                    </RuleIconBox>
                    <RuleText>
                        <strong>Produtor indicado</strong>
                        <span>1% sobre os 3 primeiros eventos, dentro de 1 ano da aprovação.</span>
                    </RuleText>
                </RuleItem>

                <RuleItem>
                    <RuleIconBox>
                        <Store size={17} color={panelAccent(colors.secondaryPurple)} />
                    </RuleIconBox>
                    <RuleText>
                        <strong>Estabelecimento indicado</strong>
                        <span>30%, uma única vez, sobre a primeira assinatura de plano, dentro de 3 meses da aprovação.</span>
                    </RuleText>
                </RuleItem>

                <RuleFootnote>
                    As comissões são apuradas pelo time comercial dentro do prazo de cada indicação.
                </RuleFootnote>
            </Card>

            <ListHeader>
                <SectionTitle>Suas indicações</SectionTitle>
                {
                    !!referrals.length && (
                        <CountChip>{activeCount} {activeCount === 1 ? 'ativa' : 'ativas'}</CountChip>
                    )
                }
            </ListHeader>

            {
                summaryError ? (
                    <Card>
                        <StateBox>
                            <StateIconBox danger>
                                <AlertCircle size={22} color={colors.danger} />
                            </StateIconBox>
                            <StateTitle>{summaryError}</StateTitle>
                            <GhostButton onClick={loadSummary}>
                                <RefreshCw size={14} />
                                Tentar novamente
                            </GhostButton>
                        </StateBox>
                    </Card>
                ) : !referrals.length ? (
                    <Card>
                        <StateBox>
                            <StateIconBox>
                                <Users size={22} color={panelAccent(colors.secondaryPurple)} />
                            </StateIconBox>
                            <StateTitle>Você ainda não tem indicações aprovadas.</StateTitle>
                            <StateHint>
                                Uma indicação só aparece aqui depois que o cadastro do parceiro for aprovado pela nossa
                                equipe, o que pode levar alguns dias após ele se cadastrar pelo seu link.
                            </StateHint>
                        </StateBox>
                    </Card>
                ) : (
                    <ReferralsList>
                        {referrals.map((referral) => {
                            const TypeIcon = PARTNER_TYPE_ICON[referral.partner_type] || Users

                            return (
                                <ReferralRow key={referral.id}>
                                    <TypeIconBox $expired={referral.status === 'expired'}>
                                        <TypeIcon
                                            size={18}
                                            color={referral.status === 'expired' ? colors.night : panelAccent(colors.secondaryPurple)}
                                        />
                                    </TypeIconBox>

                                    <ReferralInfo>
                                        <ReferralName>{referral.partner_name}</ReferralName>
                                        <ReferralMeta>
                                            {PARTNER_TYPE_LABEL[referral.partner_type]} · Aprovado em {date.format(referral.activated_at)}
                                        </ReferralMeta>
                                        <ReferralMeta>
                                            {
                                                referral.status === 'active'
                                                    ? `Válida até ${date.format(referral.expires_at)}`
                                                    : `Expirou em ${date.format(referral.expires_at)}`
                                            }
                                        </ReferralMeta>
                                    </ReferralInfo>

                                    <ReferralValue>
                                        <StatusBadge $badgeColor={STATUS_COLOR[referral.status]}>
                                            {STATUS_LABEL[referral.status]}
                                        </StatusBadge>
                                    </ReferralValue>
                                </ReferralRow>
                            )
                        })}
                    </ReferralsList>
                )
            }
        </>
    )
}

const Card = styled.section`
    background: ${colors.neutralLight};
    border-radius: 14px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08), 0 4px 20px rgba(0, 0, 0, 0.04);
    padding: 18px;
    margin-bottom: 14px;

    @media screen and (min-width: ${breakpoints.sm}) {
        padding: 20px;
    }
`;

const CardTop = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 12px;
    margin-bottom: 14px;
`;

const AccentIconBox = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: ${panelAccentAlpha(colors.secondaryPurple, '14')};
    flex-shrink: 0;
`;

const CardTitle = styled.h3`
    margin: 0;
    font-size: 15px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const CardHint = styled.p`
    margin: 2px 0 0;
    font-size: 13px;
    line-height: 1.45;
    color: ${colors.night};
`;

const LinkField = styled.div`
    display: block;
    background: ${colors.neutralBackground};
    border: 1px solid ${colors.neutralDark};
    border-radius: 10px;
    padding: 12px 14px;
    font-size: 13px;
    font-weight: 500;
    color: ${colors.nightDark};
    white-space: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
`;

const ShareActions = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: 12px;
`;

const BaseButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    flex: 1 1 160px;
    min-height: 44px;
    padding: 0 18px;
    border-radius: 10px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s ease, border-color 0.15s ease;

    @media screen and (min-width: ${breakpoints.sm}) {
        flex: 0 0 auto;
    }
`;

const PrimaryButton = styled(BaseButton)`
    border: 1px solid ${({ $done }) => $done ? ACTIVE_GREEN : panelAccent(colors.secondaryPurple)};
    background: ${({ $done }) => $done ? ACTIVE_GREEN : panelAccent(colors.secondaryPurple)};
    color: ${colors.neutralLight};

    &:hover {
        background: ${({ $done }) => $done ? ACTIVE_GREEN : '#6d1fc7'};
    }
`;

const SecondaryButton = styled(BaseButton)`
    border: 1px solid ${colors.neutralDark};
    background: ${colors.neutralLight};
    color: ${colors.nightDark};

    &:hover {
        background: ${colors.neutralBackground};
    }
`;

const GhostButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    min-height: 38px;
    padding: 0 14px;
    border: 1px solid ${colors.neutralDark};
    border-radius: 8px;
    background: ${colors.neutralLight};
    color: ${colors.nightDark};
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    flex-shrink: 0;

    &:hover {
        background: ${colors.neutralBackground};
    }
`;

const CodeRow = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 14px;
    font-size: 13px;
    color: ${colors.night};
`;

const CodeChip = styled.span`
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 1.5px;
    color: ${panelAccent(colors.secondaryPurple)};
    background: ${panelAccentAlpha(colors.secondaryPurple, '12')};
    border-radius: 6px;
    padding: 4px 10px;
`;

const InlineError = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    background: ${colors.danger}0A;
    border: 1px solid ${colors.danger}33;
    border-radius: 10px;
    padding: 14px;
`;

const InlineErrorText = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1 1 200px;
    gap: 2px;

    strong {
        font-size: 13px;
        font-weight: 600;
        color: ${colors.nightDark};
    }

    span {
        font-size: 13px;
        color: ${colors.night};
    }
`;

const SectionLabel = styled.h3`
    margin: 0 0 14px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: ${colors.night};
`;

const RuleItem = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 12px;

    & + & {
        margin-top: 14px;
    }
`;

const RuleIconBox = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 9px;
    background: ${panelAccentAlpha(colors.secondaryPurple, '12')};
    flex-shrink: 0;
`;

const RuleText = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;

    strong {
        font-size: 14px;
        font-weight: 600;
        color: ${colors.nightDark};
    }

    span {
        font-size: 13px;
        line-height: 1.45;
        color: ${colors.night};
    }
`;

const RuleFootnote = styled.p`
    margin: 14px 0 0;
    padding-top: 12px;
    border-top: 1px solid ${colors.neutralDark};
    font-size: 12px;
    line-height: 1.45;
    color: ${colors.night};
`;

const ListHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin: 22px 0 12px;
`;

const SectionTitle = styled.h2`
    margin: 0;
    font-size: 17px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const CountChip = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: ${ACTIVE_GREEN};
    background: ${colors.success}1A;
    border-radius: 50px;
    padding: 4px 10px;
    white-space: nowrap;
`;

const StateBox = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 10px;
    padding: 14px 6px;
    text-align: center;
`;

const StateIconBox = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background: ${({ danger }) => danger ? `${colors.danger}12` : `${panelAccentAlpha(colors.secondaryPurple, '12')}`};
`;

const StateTitle = styled.p`
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const StateHint = styled.p`
    margin: 0;
    max-width: 420px;
    font-size: 13px;
    line-height: 1.5;
    color: ${colors.night};
`;

const ReferralsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const ReferralRow = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    background: ${colors.neutralLight};
    border-radius: 12px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08), 0 4px 20px rgba(0, 0, 0, 0.04);
    padding: 14px 16px;
`;

const TypeIconBox = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 38px;
    height: 38px;
    border-radius: 10px;
    background: ${({ $expired }) => $expired ? `${colors.night}12` : `${panelAccentAlpha(colors.secondaryPurple, '12')}`};
    flex-shrink: 0;
`;

const ReferralInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
    min-width: 0;
`;

const ReferralName = styled.p`
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
    overflow-wrap: anywhere;
`;

const ReferralMeta = styled.p`
    margin: 0;
    font-size: 12px;
    line-height: 1.4;
    color: ${colors.night};
`;

const ReferralValue = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 6px;
    flex-shrink: 0;
`;

const StatusBadge = styled.span`
    font-size: 11px;
    font-weight: 600;
    padding: 3px 9px;
    border-radius: 50px;
    background: ${({ $badgeColor }) => $badgeColor}18;
    color: ${({ $badgeColor }) => $badgeColor};
    white-space: nowrap;
`;
